import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";

import { listTenants } from "@/api/tenants";
import { Checkbox, Field, Input, Select } from "@/components/form";
import { ErrorBox } from "@/components/Spinner";
import { Button, EmptyState } from "@/components/ui";
import type { MessagingAudienceSelector, MessagingSourceKind } from "@/types/api";

const KIND_LABELS: Record<MessagingSourceKind, string> = {
  tenant_users: "Tenant users",
  tenant_admins: "Tenant admins",
  operators: "Operators (Hapsout)",
};

function parseList(value: string): string[] | null {
  const items = value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return items.length ? items : null;
}

/**
 * The audience is built exclusively from pickers — never a typed address or
 * a typed tenant identifier. Tenants come from the same active-tenant list
 * as the rest of the console; leaving the picker empty means "every active
 * tenant" rather than forcing the operator to select each one by hand.
 */
export function AudienceBuilder({
  value,
  onChange,
  disabled,
  token,
}: {
  value: MessagingAudienceSelector[];
  onChange: (next: MessagingAudienceSelector[]) => void;
  disabled?: boolean;
  token: string;
}) {
  const tenants = useQuery({
    queryKey: ["tenants", "list", "active"],
    queryFn: () => listTenants(token, "active"),
    enabled: !!token,
  });
  const tenantOptions = tenants.data?.items ?? [];

  const update = (index: number, patch: Partial<MessagingAudienceSelector>) =>
    onChange(value.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));
  const add = () => onChange([...value, { kind: "tenant_users", tenantIds: null, stages: null, filter: null, confirmedOnly: true }]);

  return (
    <div className="space-y-3">
      {tenants.isError && <ErrorBox error={tenants.error} />}
      {value.length === 0 && (
        <EmptyState size="inline" title="No source yet.">
          Add at least one.
        </EmptyState>
      )}
      {value.map((selector, index) => (
        <div key={index} className="rounded border border-line p-3">
          <div className="flex items-center gap-3">
            <Field label={`Source ${index + 1} type`} hideLabel className="min-w-0 flex-1">
              <Select
                value={selector.kind}
                disabled={disabled}
                onChange={(e) => {
                  const kind = e.target.value as MessagingSourceKind;
                  update(
                    index,
                    kind === "operators"
                      ? { kind, tenantIds: null, stages: null, filter: null, confirmedOnly: undefined }
                      : { kind, tenantIds: selector.tenantIds ?? null, stages: selector.stages ?? null, filter: selector.filter ?? null, confirmedOnly: selector.confirmedOnly ?? true },
                  );
                }}
                options={(Object.keys(KIND_LABELS) as MessagingSourceKind[]).map((k) => ({
                  value: k,
                  label: KIND_LABELS[k],
                }))}
              />
            </Field>
            <span className="text-xs text-ink-muted">#{index + 1}</span>
            <Button variant="ghost" size="sm" icon={Trash2} className="ml-auto" disabled={disabled} onClick={() => remove(index)}>
              Remove
            </Button>
          </div>
          {selector.kind !== "operators" && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field label="Tenants (none selected = every active tenant)">
                <Select
                  multiple
                  className="h-28"
                  disabled={disabled || tenants.isLoading}
                  value={selector.tenantIds ?? []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                    update(index, { tenantIds: selected.length ? selected : null });
                  }}
                  options={tenantOptions.map((t) => ({
                    value: t.tenantId,
                    label: `${t.displayName} (${t.tenantId})`,
                  }))}
                />
              </Field>
              {selector.kind === "tenant_users" && (
                <>
                  <Field label="Stages (comma-separated, empty = all)">
                    <Input
                      disabled={disabled}
                      value={(selector.stages ?? []).join(", ")}
                      onChange={(e) => update(index, { stages: parseList(e.target.value) })}
                      placeholder="production"
                    />
                  </Field>
                  <Field label="Filter">
                    <Select
                      disabled={disabled}
                      value={selector.filter ?? ""}
                      onChange={(e) => update(index, { filter: e.target.value ? "mfa_not_enabled" : null })}
                      options={[
                        { value: "", label: "Everyone" },
                        { value: "mfa_not_enabled", label: "Two-factor authentication not enabled" },
                      ]}
                    />
                  </Field>
                  <Checkbox
                    className="self-end"
                    label="Only people who accepted their invitation"
                    disabled={disabled}
                    checked={selector.confirmedOnly ?? true}
                    onChange={(e) => update(index, { confirmedOnly: e.target.checked })}
                  />
                </>
              )}
            </div>
          )}
        </div>
      ))}
      <Button variant="secondary" icon={Plus} disabled={disabled || value.length >= 10} onClick={add}>
        Add source
      </Button>
    </div>
  );
}
